/**
 * Listings are a lightweight pattern for storing a subset of a list with pagination information
 */

export interface Pagination {
  offset: number;
  limit: number;
  total: number;
  isExact?: boolean;
}

export interface ListingPagination extends Pagination {
  totalWithHidden: number;
}

export interface Listing<Result, Param> {
  isLoading: boolean;
  // A stale listing is one that has been explicitly marked as out-of-date but hasn't been
  // re-fetched. For example, if 10 workspace tabs have an active containers-for-batch listing and
  // we do an operation that affects the listings, we want to lazily reload the listings rather than
  // fetching them all right away, so we mark them as stale and let the components re-fetch as
  // necessary.
  isStale: boolean;
  loadSuccess: boolean;
  // A null `items` indicates that one more more of the items has been invalidated. This is a bit of a hack.
  // since we invalidate items by setting them to null without removing their ids from the store, a
  // component which uses the ids to get the items from the store will then receive a listing with some
  // null items. Instead we return a null for items indicating to the component to wait for a refresh.
  items: Array<Result> | null;
  pagination: ListingPagination | null;
  param: Param;
}

export function listingStart<Result, Param>(param: Param): Listing<Result, Param> {
  return {
    isLoading: true,
    isStale: false,
    loadSuccess: false,
    items: null,
    pagination: null,
    param,
  };
}

export function listingRestart<Result, Param>(listing: Listing<Result, Param>): Listing<Result, Param> {
  return {
    ...listing,
    isStale: false,
    isLoading: true,
  };
}

export function listingMarkStale<Result, Param>(listing: Listing<Result, Param>): Listing<Result, Param> {
  return {
    ...listing,
    isStale: true,
  };
}

export function listingComplete<Result, Param>(
  listing: Listing<Result, Param>,
  items: Array<Result>,
  pagination: ListingPagination
): Listing<Result, Param> {
  return {
    ...listing,
    isLoading: false,
    isStale: false,
    loadSuccess: true,
    items,
    pagination,
  };
}

export function listingFail<Result, Param>(listing: Listing<Result, Param>): Listing<Result, Param> {
  return {
    ...listing,
    isLoading: false,
    isStale: false,
    loadSuccess: false,
    items: null,
    pagination: null,
  };
}
